import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, useNavigate, useLocation } from 'react-router-dom';
import { StyleSheetManager } from 'styled-components';
import { MotionConfig } from 'framer-motion';
import { shouldForwardMotionProp } from '../utils/styling/shouldForwardMotionProp';
import { AppContainer, AuthContainer, AuthCard, Logo } from './App.styles';

// Components
import PasswordChangeRequired from '../components/auth/PasswordChangeRequired';
import { passwordGateAction } from './passwordGate';
import AppUpdateProvider from '../components/system/AppUpdateProvider';
import ErrorBoundary from '../components/common/ErrorBoundary';
import ScrollToTop from '../components/common/ScrollToTop';
import PageSpinner from '../components/common/PageSpinner';
import logo from '../assets/logo.png';

import PublicRoutes from './PublicRoutes';
import AuthenticatedRoutes from './AuthenticatedRoutes';
import useAppSession from './useAppSession';

// Context
import { AuthProvider, useAuth } from '../context/AuthContext';
import { ThemeProvider } from '../context/ThemeContext';
import { ToastProvider } from '../components/common/ToastProvider';
import { ConfirmProvider } from '../components/common/ConfirmProvider';

const PurchaseConfirmation = lazy(() => import('../components/payment/PurchaseConfirmation'));

function AppContent() {
  const { user, loading, setUser } = useAuth();
  // Sidebar is collapsible on every breakpoint now, so default it open on
  // desktop widths (matches the old always-visible desktop behavior) and
  // closed on mobile/tablet widths (matches the old hamburger-driven behavior).
  const [sidebarOpen, setSidebarOpen] = useState(() => (
    typeof window !== 'undefined' ? window.innerWidth > 768 : true
  ));
  const navigate = useNavigate();
  const location = useLocation();

  // sidebarOpen is only initialised from the width once, so rotating a tablet or resizing a
  // window across the 768px breakpoint used to leave a desktop-open sidebar covering the page
  // as a mobile overlay (or a mobile-closed one missing on desktop). Re-sync when it crosses.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia('(max-width: 768px)');
    const onChange = (e) => setSidebarOpen(!e.matches);
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, []);

  const { idleTimer, handleAutoLogout } = useAppSession({ user, setUser, navigate });

  // Only the auth check gates rendering. sessionConfig is a second, unrelated
  // round-trip that only feeds the idle timer, and useAppSession already leaves
  // that timer disabled until it arrives (falling back to 30min idle / 60s
  // warning / 60s heartbeat). Waiting on it here held the whole app behind a
  // full-screen spinner for an extra ~227ms -- measured in production,
  // /api/auth/session-config runs 313ms to 339ms while the DOM is ready at
  // 112ms -- buying nothing.
  if (loading) {
    return (
      <AppContainer>
        <PageSpinner minHeight="100vh" />
      </AppContainer>
    );
  }

  if (!user) {
    return <PublicRoutes />;
  }

  // Check if authenticated user needs to change password. The one exception is the
  // purchase confirmation page, shown first (on its own) so a new student sees
  // "You're enrolled!" before being asked for a password; it links on to the gate.
  const passwordGate = passwordGateAction(user, location.pathname);
  if (passwordGate === 'confirmation') {
    return (
      <Suspense fallback={<PageSpinner />}>
        <PurchaseConfirmation />
      </Suspense>
    );
  }
  if (passwordGate === 'password') {
    return (
      <AuthContainer>
          <AuthCard
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Logo>
              <img src={logo} width="80" height="114" alt="Faith Insight Logo" />
              <h1>Faith Insight</h1>
              <p>Learning Portal</p>
            </Logo>
            <PasswordChangeRequired />
        </AuthCard>
      </AuthContainer>
    );
  }

  return (
    <AuthenticatedRoutes
      sidebarOpen={sidebarOpen}
      setSidebarOpen={setSidebarOpen}
      idleTimer={idleTimer}
      handleAutoLogout={handleAutoLogout}
    />
  );
}

function App() {
  // Mobile viewport height fix
  useEffect(() => {
    const setVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setVh();
    window.addEventListener('resize', setVh);
    return () => window.removeEventListener('resize', setVh);
  }, []);

  // Unregister any existing service workers to prevent FetchEvent.respondWith errors
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => {
          console.log('Unregistering service worker:', registration.scope);
          registration.unregister();
        });
      });
    }
  }, []);

  // Service worker disabled - was causing FetchEvent.respondWith errors
  // useEffect(() => {
  //   const initWorkbox = async () => {
  //     try {
  //       await workboxService.init();
  //       // Workbox initialized successfully
  //     } catch (error) {
  //       // Failed to initialize Workbox
  //     }
  //   };

  //   // Only initialize in production
  //   if (process.env.NODE_ENV === 'production') {
  //     initWorkbox();
  //   }

  //   // Listen for service worker update events
  //   const handleSWUpdate = (event) => {
  //     // Service worker update available
  //     // You can show a notification to the user here
  //   };

  //   window.addEventListener('sw-update-available', handleSWUpdate);

  //   return () => {
  //     window.removeEventListener('sw-update-available', handleSWUpdate);
  //   };
  // }, []);

  return (
    <ErrorBoundary>
      <MotionConfig reducedMotion="always">
      <StyleSheetManager shouldForwardProp={shouldForwardMotionProp}>
        <ThemeProvider>
          <ToastProvider>
            <ConfirmProvider>
              <AuthProvider>
                <AppUpdateProvider>
                  <Router basename="/portal">
                    <ScrollToTop />
                    <ErrorBoundary>
                      <AppContent />
                    </ErrorBoundary>
                  </Router>
                </AppUpdateProvider>
              </AuthProvider>
            </ConfirmProvider>
          </ToastProvider>
        </ThemeProvider>
      </StyleSheetManager>
      </MotionConfig>
    </ErrorBoundary>
  );
}

export default App;
